import { Component, For, Show, createSignal, onCleanup, onMount } from 'solid-js';
import type { Agent } from '../types';

// Import SVG icons
import coupleSvg from '../assets/agents/person/couple-mw.svg?raw';
import femaleSvg from '../assets/agents/person/female.svg?raw';
import maleSvg from '../assets/agents/person/male.svg?raw';

const SVG_MAP: Record<string, string> = {
  'male.svg': maleSvg,
  'female.svg': femaleSvg,
  'couple-mw.svg': coupleSvg,
};

interface AgentDetailsModalProps {
  agent: Agent | null;
  onClose: () => void;
}

const AgentDetailsModal: Component<AgentDetailsModalProps> = (props) => {
  const [isDragging, setIsDragging] = createSignal(false);
  const [translateY, setTranslateY] = createSignal(0);
  
  let startY = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;

  const DISMISS_THRESHOLD = 150; // px to swipe down before auto-dismiss
  const VELOCITY_THRESHOLD = 0.5; // px/ms - fast swipe triggers dismiss
  const MIN_DRAG_DISTANCE = 8; // px to move before we commit to dragging

  const handleTouchStart = (e: TouchEvent) => {
    // Don't interfere with scrolling if content is scrolled
    const scrollContainer = (e.target as HTMLElement).closest('.overflow-y-auto');
    if (scrollContainer && scrollContainer.scrollTop > 5) {
      return;
    }
    
    startY = e.touches[0].clientY;
    lastY = startY;
    lastTime = Date.now();
    velocity = 0;
    // Reset translateY to 0 when starting a new drag
    setTranslateY(0);
  };

  const handleTouchMove = (e: TouchEvent) => {
    const touchY = e.touches[0].clientY;
    const delta = touchY - startY;
    
    // Only commit to dragging after minimum movement
    if (!isDragging() && Math.abs(delta) < MIN_DRAG_DISTANCE) {
      return;
    }
    
    // Only allow downward swipes
    if (delta > 0) {
      if (!isDragging()) {
        setIsDragging(true);
      }
      
      // Add resistance as you pull further - flows with thumb
      const resistance = Math.max(0.4, 1 - delta / 800);
      setTranslateY(delta * resistance);
      
      // Calculate velocity using last position
      const timeDelta = Date.now() - lastTime;
      if (timeDelta > 0) {
        velocity = (touchY - lastY) / timeDelta;
      }
      
      lastY = touchY;
      lastTime = Date.now();
    }
  };

  const handleTouchEnd = (modalEl: HTMLDivElement) => {
    if (!isDragging()) return;
    setIsDragging(false);

    const shouldDismiss = translateY() > DISMISS_THRESHOLD || velocity > VELOCITY_THRESHOLD;

    if (shouldDismiss) {
      // Animate out with spring
      animateOut(modalEl);
    } else {
      // Spring back to position
      animateBack(modalEl);
    }
  };

  const animateOut = (modalEl: HTMLDivElement) => {
    // Use CSS transition for smooth spring-like animation
    modalEl.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
    modalEl.style.transform = 'translateY(100%)';
    
    setTimeout(() => {
      props.onClose();
      setTranslateY(0); // Reset after close
    }, 300);
  };

  const animateBack = (modalEl: HTMLDivElement) => {
    // Spring back with bounce
    modalEl.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
    modalEl.style.transform = 'translateY(0)';
    
    setTimeout(() => {
      setTranslateY(0);
      modalEl.style.transition = '';
    }, 400);
  };

  return (
    <Show when={props.agent}>
      {(agent) => {
        let modalRef: HTMLDivElement | undefined;

        onMount(() => {
          if (!modalRef) return;

          const onTouchStart = (e: TouchEvent) => handleTouchStart(e);
          const onTouchMove = (e: TouchEvent) => handleTouchMove(e);
          const onTouchEnd = () => handleTouchEnd(modalRef!);

          modalRef.addEventListener('touchstart', onTouchStart, { passive: true });
          modalRef.addEventListener('touchmove', onTouchMove, { passive: true });
          modalRef.addEventListener('touchend', onTouchEnd);

          onCleanup(() => {
            if (modalRef) {
              modalRef.removeEventListener('touchstart', onTouchStart);
              modalRef.removeEventListener('touchmove', onTouchMove);
              modalRef.removeEventListener('touchend', onTouchEnd);
            }
          });
        });

        return (
          <>
            {/* Modal */}
            <div 
              ref={modalRef}
              class="fixed inset-x-0 bottom-0 h-[80vh] bg-white rounded-t-2xl shadow-2xl z-[2001] md:hidden animate-slide-up"
              style={{
                transform: isDragging() ? `translateY(${translateY()}px)` : 'translateY(0)',
                transition: isDragging() ? 'none' : ''
              }}
            >
            <div class="flex flex-col h-full select-none">
              {/* Swipe handle */}
              <div class="flex justify-center pt-2 pb-1">
                <div class="w-12 h-1 bg-gray-300 rounded-full" />
              </div>

              {/* Header */}
              <div class="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 flex-shrink-0 flex items-center justify-center overflow-hidden"
                    style="width: 32px; height: 32px;"
                    innerHTML={(() => {
                      const svg = agent().svg;
                      if (svg && SVG_MAP[svg]) {
                        return SVG_MAP[svg];
                      }
                      return `<span style="font-size: 28px; line-height: 1;">${agent().emoji}</span>`;
                    })()}
                  />
                  <h2 class="text-xl font-bold text-gray-900">
                    {agent().name || agent().label}
                  </h2>
                </div>
                <button
                  class="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  onClick={props.onClose}
                >
                  <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content - scrollable, not draggable */}
              <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {/* Track */}
                <Show when={agent().track}>
                  <div>
                    <For each={agent().track!.split('\n').filter(p => p.trim())}>
                      {(paragraph, i) => (
                        <p class={i() > 0 ? "mt-2 text-base text-gray-800 leading-relaxed" : "text-base text-gray-800 leading-relaxed"}>
                          {paragraph}
                        </p>
                      )}
                    </For>
                  </div>
                </Show>

                {/* Testimony */}
                <Show when={agent().testimony}>
                  <div>
                    <span class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Vittnesmål:</span>
                    <For each={agent().testimony!.split('\n').filter(p => p.trim())}>
                      {(paragraph, i) => (
                        <p class={i() > 0 ? "mt-2 text-base text-gray-800 leading-relaxed" : "mt-0.5 text-base text-gray-800 leading-relaxed"}>
                          {paragraph}
                        </p>
                      )}
                    </For>
                  </div>
                </Show>

                {/* Agent Images - from sources with image URLs */}
                <Show when={agent().sources && agent().sources!.some(s => {
                  const url = s.url.toLowerCase();
                  return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.webp');
                })}>
                  <div class="flex flex-wrap gap-3">
                    <For each={agent().sources!.filter(s => {
                      const url = s.url.toLowerCase();
                      return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.webp');
                    })}>
                      {(source) => (
                        <img 
                          src={source.url.startsWith('http') ? source.url : `/people/${source.url}`}
                          alt={source.name || 'Agent image'}
                          class="w-20 h-20 object-cover rounded border border-gray-200"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    </For>
                  </div>
                </Show>

                {/* Sources */}
                <Show when={agent().sources && agent().sources!.some(s => {
                  const url = s.url.toLowerCase();
                  return s.name && s.url && !(url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.webp'));
                })}>
                  <div class="pt-2">
                    <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Källor
                    </h3>
                    <div class="space-y-2">
                      <For each={agent().sources!.filter(s => {
                        const url = s.url.toLowerCase();
                        return s.name && s.url && !(url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.webp'));
                      })}>
                        {(source) => (
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center gap-2 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg class="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            <span class="text-blue-700 font-medium">{source.name}</span>
                          </a>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>

                {/* Empty state */}
                <Show when={!agent().track && !agent().testimony && (!agent().sources || agent().sources!.length === 0)}>
                  <div class="py-12 text-center">
                    <p class="text-gray-500">Ingen ytterligare information tillgänglig</p>
                  </div>
                </Show>
              </div>
            </div>
            </div>
          </>
        );
      }}
    </Show>
  );
};

export default AgentDetailsModal;
